// Review from the keyboard: j / k move, Enter opens, Esc closes, K keeps, R removes (asks the reason), A approves.
// Shift is part of K, R and A on purpose: a stray lower-case key never sends or removes.
import { useEffect, useRef } from "react";

export type KeyAction =
  | "next"
  | "prev"
  | "open"
  | "close"
  | "keep"
  | "remove"
  | "approve";

const KEYS: Record<string, KeyAction> = {
  j: "next",
  k: "prev",
  Enter: "open",
  Escape: "close",
  K: "keep",
  R: "remove",
  A: "approve",
};

type Key = Pick<
  KeyboardEvent,
  "key" | "target" | "metaKey" | "ctrlKey" | "altKey"
>;

/** The action for a key press, or null while typing, with a modifier, or on Enter over a button or link */
export const keyAction = (event: Key): KeyAction | null => {
  if (event.metaKey || event.ctrlKey || event.altKey) return null;
  const target = event.target as HTMLElement | null;
  const tag = target?.tagName ?? "";
  if (
    ["INPUT", "TEXTAREA", "SELECT"].includes(tag) ||
    target?.isContentEditable
  )
    return null;
  if (event.key === "Enter" && ["BUTTON", "A"].includes(tag)) return null;
  return KEYS[event.key] ?? null;
};

/** Calls onAction for the review keys pressed anywhere on the page */
export const usePreLeadKeys = (onAction: (action: KeyAction) => void) => {
  const handler = useRef(onAction);
  handler.current = onAction;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // a dialog (the Remove reason, Add by hand, the mobile drawer) owns the keyboard while it is open
      if (document.querySelector('[role="dialog"]')) return;
      const action = keyAction(event);
      if (!action) return;
      event.preventDefault();
      handler.current(action);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
};
